import $ from 'jquery';
import {getSetting} from '@wc-stripe/utils';
import CheckoutController from './CheckoutController';

/**
 * CheckoutSessionController - pairs with CheckoutSessionGateway to drive Adaptive Pricing's
 * single shared payment element on the checkout page.
 *
 * Every other controller resolves its container from a fixed `this.selector` set once at
 * construction, because it only ever represents one gateway. This pairing represents whichever
 * gateway is currently selected (CheckoutSessionGateway.id is a live getter), so the container
 * has to be re-resolved from the gateway's own selector/paymentMethodType map on every call
 * instead of a selector fixed at construction time.
 *
 * CheckoutController.initActions() has the same problem for its DOM bindings - it bakes
 * `this.gateway.id` into selector strings once, which would freeze them to whichever gateway
 * happened to be selected at construction time. Instead of binding once for "the" gateway, this
 * binds once per AP-eligible gateway id (from the paymentMethods setting), all pointing at the
 * same inherited handlers - those handlers already read `this.gateway.id` dynamically, so as long
 * as the event actually comes from that gateway's own row, they resolve correctly.
 *
 * @since 4.1.0
 */
class CheckoutSessionController extends CheckoutController {

    initActions() {
        this.actions.addAction('updated_checkout', this.onUpdatedCheckout.bind(this));
        this.actions.addAction('payment_method_selected', this.onPaymentMethodSelected.bind(this));
        this.actions.addAction('payment_intent_requires_actions', this.onPaymentRequiresAction.bind(this));
        this.actions.addAction('setup_intent_requires_actions', this.onPaymentRequiresAction.bind(this));
        this.actions.addAction('checkout_order_review', this.onCheckoutOrderReview.bind(this));

        Object.entries(getSetting('paymentMethods', {}))
            .filter(([, data]) => data.enabled)
            .forEach(([id]) => this.bindGatewayEvents(id));
    }

    /**
     * @param {string} id - A registered Stripe gateway id (e.g. stripe_cc, stripe_ideal).
     * @returns {void}
     */
    bindGatewayEvents(id) {
        $(document.body).on('change', `[name="wc-${id}-payment-token"]`, this.onPaymentTokenChange.bind(this));
        $(document.body).on('change', `[name="wc-${id}-new-payment-method"]`, this.onSavePaymentMethodChange.bind(this));
        $(document.body).on('click', `li.payment_method_${id} .wc-stripe-saved-method, li.payment_method_${id} .woocommerce-SavedPaymentMethods-new`, this.onSavedTokenChanged.bind(this));
        $(document.body).on('click', `li.payment_method_${id} .wc-stripe-saved-method__see-more`, this.onSeeMoreClick.bind(this));
        if (this.context.isCheckout()) {
            $('form.checkout').on(`checkout_place_order_${id}`, this.onCheckoutPlaceOrder.bind(this));
            this.actions.addAction(`checkout_error_${id}`, this.onCheckoutError.bind(this));
        }
    }

    onPaymentMethodSelected(gatewayId) {
        this.gateway.onPaymentMethodSelected(gatewayId);
        super.onPaymentMethodSelected(gatewayId);
    }

    getElementContainer() {
        const target = this.gateway.getGatewayTarget(this.gateway.id);
        if (!target) {
            return null;
        }
        const el = document.querySelector(target.selector);
        return this.actions.applyFilters('get_element_container', el, this);
    }

}

export default CheckoutSessionController;
export {CheckoutSessionController};